import React, { useEffect, useMemo, useRef, useState } from 'react';
import {Alert, Autocomplete, Box, Checkbox, Chip, IconButton, LinearProgress, List, ListItem, ListItemText, Stack, TextField, Tooltip, Typography, styled} from "@mui/material";
import _ from "lodash";
import Div from "@jumbo/shared/Div";
import { Draggable, Droppable } from "react-beautiful-dnd";
import JumboCardQuick from '@jumbo/components/JumboCardQuick';
import JumboScrollbar from '@jumbo/components/JumboScrollbar';
import CalendarMonthTwoToneIcon from '@mui/icons-material/CalendarMonthTwoTone';
import moment from 'moment';
import PlaceIcon from '@mui/icons-material/Place';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined';
import { LoadingButton } from '@mui/lab';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { StyledDatePicker } from 'app/widgets/Shared/StyledDatePicker/StyledDatePicker';
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import { useCompanySettings } from 'app/providers/CompanySettingsProvider/CompanySettingsProvider';
import { Search, SearchIconWrapper, StyledInputBase } from 'app/shared/SearchGlobal/style';
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import { getDropoffCarrierByKey, getDropoffCarrierDisplay } from 'app/utils/dropoffCarriers';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import OrderSourceBadges from 'app/shared/OrderSourceBadges';
import { erpDeliveryInfo } from 'app/utils/erpDeliveryDate';

const SEARCH_DEBOUNCE_MS = 350;

const DNDList = styled(List)(({theme, isDraggingOver}) => ({
    padding: theme.spacing(1),
    background: isDraggingOver ? "#E0F4FF" : "inherit",
}));

const OrdersDND = props => {
    const { companySettings } = useCompanySettings();
    const dropoffCarrierCatalog = companySettings?.available_dropoff_carriers;
    const {
        title,
        items,
        listLoading,
        droppableId,
        newGroupContainer,
        groupNumber,
        groupColor,
        showSearch=false,
        leftActions,
        rightActions,
        isPlanification=false,
        selShipDate=null,
        selDriver=null,
        selVehicle=null,
        mapPickupMode = null,
        drivers=[],
        transporters=[],
        vehicles=[],
        selectedBoxes=[],
        // Checkbox por pedido para el modo "Pedidos" del optimizador. Es una
        // selección aparte de selectedBoxes (que es la de arrastrar cajas).
        showItemCheckbox = false,
        checkedItemIds = [],
        checkableItemIds = null,
        uncheckableReason = '',
        onToggleItemChecked = () => {},
        onToggleSelection = () => {},
        onToggleSelectionInGroup = () => {},
        onItemMouseOver = () => {},
        onItemMouseLeave = () => {},
        onShipDateChange = () => {},
        onSelDriverChange = () => {},
        onSelVehicleChange = () => {},
        onEndPlanification = () => {},
        searchValue = undefined,
        onSearchQueryChange = null,
        enableInfiniteScroll = false,
        hasMoreItems = false,
        loadingMoreItems = false,
        onReachListEnd = () => {}
    } = props;
    const [saving, setSaving] = useState(false);
    const [query, setQuery] = useState('');
    const isRoute = droppableId?.startsWith('plannedOrders_');
    const endReachedRef = useRef(false);
    const effectiveQuery = typeof searchValue === 'string' ? searchValue : query;
    const isFieldEnabled = (field) => companySettings?.[field] !== false;
    const showDriverField = isFieldEnabled('plan_require_driver');
    const showVehicleField = isFieldEnabled('plan_require_vehicle');
    const showTransporterField = isFieldEnabled('plan_require_transportation_company');
    const showTripAssigneeField = showDriverField || showTransporterField;
    const showCombinedTripAssigneeField = showDriverField && showTransporterField;
    const showDropoffCarrierField = isFieldEnabled('plan_require_dropoff_carrier');
    const planningButtonDisabled = saving
        || (showTripAssigneeField && !selDriver)
        || (showVehicleField && !selVehicle);
    const tripAssigneeOptions = showCombinedTripAssigneeField
        ? _.uniqBy([...drivers, ...transporters], 'id')
        : showTransporterField
            ? transporters
            : drivers;
    const tripAssigneeLabel = showCombinedTripAssigneeField
        ? 'Conductor / Transportista'
        : showTransporterField
            ? 'Transportista'
            : 'Conductor';
    const planningFieldSx = {
        flex: '1 1 160px',
        minWidth: 0,
    };
    const planningVehicleFieldSx = {
        ...planningFieldSx,
        flex: '1 1 180px',
    };
    const planningDateSx = {
        background: 'white',
        width: 140,
        flex: '0 0 140px',
    };

    const isSelected = (idx) => {
        return _.find(selectedBoxes, { index: idx });
    };

    const boxStyle = (isDragging, isSelected) => {
        if (isDragging || isSelected) {
            return {
                background: '#F3EEEA',
                boxShadow:  '2px 2px 10px 0px rgba(0,0,0,0.3)',
                borderColor: '#888888',
            }
        } else {
            return {
                borderColor: '#DDDDDD',
            }
        }
    }
    
    // Determines if the platform specific toggle selection in group key was used
    const wasToggleInSelectionGroupKeyUsed = (event) => {
        const isUsingWindows = navigator.platform.indexOf('Win') >= 0;
        return isUsingWindows ? event.ctrlKey : event.metaKey;
    };

    // Determines if the multiSelect key was used
    const wasMultiSelectKeyUsed = (event) => event.shiftKey;

    const onItemTouchEnd = (e, droppableId, idx) => {
        if (e.defaultPrevented) {
            return;
        }
        // marking the event as used
        // we would also need to add some extra logic to prevent the click
        // if this element was an anchor
        e.preventDefault();
        onToggleSelectionInGroup(droppableId, idx);
    }

    const onItemClick = (e, droppableId, idx) => {
        if (e.defaultPrevented) {
          return;
        }
    
        if (e.button !== 0) {
          return;
        }
    
        // marking the event as used
        e.preventDefault();

        if (wasToggleInSelectionGroupKeyUsed(e)) {
            onToggleSelectionInGroup(droppableId, idx);
            return;
        }
    
        // if (wasMultiSelectKeyUsed(event)) {
        //     multiSelectTo(task.id);
        //     return;
        // }
    
        onToggleSelection(droppableId, idx);
    };

    // Pedido de ML "acordar con el vendedor" ya definido con direccion (flota propia o
    // courier): el backend lo deja con shipping_method 'agreed' y la direccion acordada en
    // planned_address, que llega como effective_location. Ver Orders::AgreedShipping.
    const isAgreedAddress = (order) => order?.shipping_method === 'agreed' && Boolean(order?.effective_location);

    const filteredItems = () => {
        return _.filter(items, (item) => {
            const attrs = [
                item.code,
                item.locatable?.name,
                (item.effective_location || item.locatable?.location)?.short_zone,
                isAgreedAddress(item) ? item.effective_location?.short_address : null,
                item.comment
            ];
            return _.some(attrs, (attr) => { return _.includes(attr?.toLowerCase(), effectiveQuery.toLowerCase()) });
        });
    }

    const getTripAssigneeOptionLabel = (option) => {
        if (!option) return '';
        if (_.isString(option)) return option;

        const fullName = [option.first_name, option.last_name].filter(Boolean).join(' ');
        return `${option.code ? option.code : "S/C"} - ${fullName}`;
    };

    const handleSearchQueryChange = (newVal) => {
        if (typeof onSearchQueryChange === 'function') {
            onSearchQueryChange(newVal);
        }
        if (typeof searchValue !== 'string') {
            setQuery(newVal);
        }
    }
    // El input tiene su propio estado y la busqueda sale con debounce. Antes el debounce se
    // creaba de nuevo en cada render, asi que no agrupaba nada (una consulta por tecla), y el
    // input mostraba el valor del padre, que llegaba tarde y se comia letras.
    const [inputValue, setInputValue] = useState(effectiveQuery || '');
    const searchChangeRef = useRef(handleSearchQueryChange);
    searchChangeRef.current = handleSearchQueryChange;
    // Ultimo valor que mando este input: cuando vuelve del padre no se pisa lo que se siguio
    // escribiendo mientras tanto.
    const lastSentRef = useRef(effectiveQuery || '');
    const debounceSearchQueryChange = useMemo(
        () => _.debounce((val) => { lastSentRef.current = val; searchChangeRef.current(val); }, SEARCH_DEBOUNCE_MS),
        []
    );
    useEffect(() => () => debounceSearchQueryChange.cancel(), [debounceSearchQueryChange]);
    // Si el padre cambia la busqueda por su cuenta (p. ej. la restaura al entrar), el input la refleja.
    useEffect(() => {
        if (typeof searchValue !== 'string' || searchValue === lastSentRef.current) return;
        lastSentRef.current = searchValue;
        setInputValue(searchValue);
    }, [searchValue]);

    useEffect(() => {
        endReachedRef.current = false;
    }, [Array.isArray(items) ? items.length : 0, hasMoreItems, loadingMoreItems, listLoading, effectiveQuery, enableInfiniteScroll]);

    const handleScrollFrame = (values) => {
        if (!enableInfiniteScroll) return;
        if (typeof onReachListEnd !== 'function') return;
        if (listLoading || loadingMoreItems || !hasMoreItems) return;
        if (effectiveQuery?.trim()) return;

        const scrollTop = Number(values?.scrollTop || 0);
        const clientHeight = Number(values?.clientHeight || 0);
        const scrollHeight = Number(values?.scrollHeight || 0);

        if (scrollHeight <= clientHeight + 8) return;

        const nearBottom = (scrollTop + clientHeight) >= (scrollHeight - 120);

        if (nearBottom && !endReachedRef.current) {
            endReachedRef.current = true;
            onReachListEnd();
            return;
        }

        if (!nearBottom) {
            endReachedRef.current = false;
        }
    };

    return (
        <JumboCardQuick
            title={
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant={"h5"} mb={0}>{title}{groupNumber && ` ${groupNumber}` }</Typography>
                        {leftActions}
                    </Stack>
                    {showSearch &&
                        <TextField
                            id="input-with-sx"
                            label="Buscar"
                            size="small"
                            value={inputValue}
                            onChange={(e) => { setInputValue(e.target.value); debounceSearchQueryChange(e.target.value); }}
                            variant="outlined" />
                    }
                    {showDropoffCarrierField ? rightActions : null}
                </Stack>
            }
            headerSx={{ px: 1 }}
            noWrapper
            sx={{mb: 2}}>
            { items &&
                <JumboScrollbar
                    autoHeight                    
                    autoHeightMax={700}
                    onScrollFrame={handleScrollFrame}>
                    <Div sx={{width: '100%', display: listLoading ? 'block' : 'none'}}>
                        <LinearProgress  />
                    </Div>
                    {filteredItems().length === 0 &&
                        <Typography variant="body2" color="textSecondary" align="center" mt={2}>No hay datos</Typography>
                    }
                    {isPlanification &&
                        <Box
                            sx={{
                                px: 1,
                                mt: 1,
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 1,
                            }}
                        >
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DesktopDatePicker
                                    label="Fecha"
                                    format="dd/MM"
                                    freeSolo
                                    blurOnSelect
                                    sx={planningDateSx}
                                    value={selShipDate}
                                    // minDate={new Date()}
                                    closeOnSelect={false}
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                        },
                                        actionBar: {
                                            actions: ['clear', 'accept'],
                                        },
                                    }}
                                    onAccept={(newValue) => onShipDateChange(newValue)}
                                    renderInput={(params) => <TextField {...params} />}
                                />
                            </LocalizationProvider>
                            {showVehicleField && (
                                <Autocomplete
                                    id="vehicle-select"
                                    size="small"
                                    freeSolo
                                    blurOnSelect
                                    fullWidth
                                    sx={planningVehicleFieldSx}
                                    options={vehicles}
                                    getOptionLabel={(option) => `${option.code ? option.code : "S/C"} - ${option.transport_unit1?.ppu}${option.transport_unit2 ? `/${option.transport_unit2.ppu}` : ''}`}
                                    value={selVehicle}
                                    onChange={(e, v) => { onSelVehicleChange(e, v); }}
                                    renderInput={(params) => <TextField {...params} label="Vehículo" sx={{background: 'white'}} />}
                                />
                            )}
                            {showTripAssigneeField && (
                                <Autocomplete
                                    id="trip-assignee-select"
                                    size="small"
                                    freeSolo
                                    blurOnSelect
                                    fullWidth
                                    sx={planningFieldSx}
                                    options={tripAssigneeOptions}
                                    getOptionLabel={getTripAssigneeOptionLabel}
                                    isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                                    value={selDriver}
                                    onChange={(e, v) => { onSelDriverChange(e, v); }}
                                    renderInput={(params) => <TextField {...params} label={tripAssigneeLabel} sx={{background: 'white'}} />}
                                />
                            )}
                            <Tooltip title="Finalizar planificación" sx={{ justifyContent: 'center' }}>
                                <IconButton
                                    value="check"
                                    variant="contained"
                                    disabled={planningButtonDisabled}
                                    color="success"
                                    size="small"
                                    sx={{ flex: '0 0 auto' }}
                                    onClick={(e) => { onEndPlanification(e); }}>
                                    <SendOutlinedIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    }
                    <Droppable droppableId={droppableId}>
                        {(provided, snapshot) => (
                            <DNDList disablePadding {...provided.droppableProps} ref={provided.innerRef} isDraggingOver={snapshot.isDraggingOver}>
                                {/* { (!items || items.length === 0) &&
                                    <ListItem
                                    dense={true}
                                    key={`empty`}
                                    ref={provided.innerRef}>
                                        <ListItemText
                                            secondary={
                                                <Stack direction="row" justifyContent="space-between">
                                                    <Typography>Sin datos</Typography>
                                                </Stack>
                                            }
                                        />
                                    </ListItem>
                                } */}
                                {_.map(items, (order,i) => {
                                        const hasMissingBarcode = order.order_details?.some(
                                            od => !od.product_barcode
                                        );

                                        return (
                                            <Draggable key={`pendingOrder_${order.id}`} draggableId={`pendingOrder_${order.id}`} index={i}>
                                                {(provided, snapshot) => {
                                                    const isHidden = !_.includes(filteredItems(), order);
                                                    return (
                                                        <ListItem
                                                            key={`ol_${order.id}`}
                                                            dense={true}
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            isDragging={snapshot.isDragging}
                                                            onMouseEnter={(e) => onItemMouseOver(e, order)}
                                                            onMouseLeave={(e) => onItemMouseLeave(e, order)}
                                                            onClick={(e) => onItemClick(e, droppableId, i)}
                                                            onTouchEnd={(e) => onItemTouchEnd(e, droppableId, i)}
                                                            sx={{
                                                                border: 1,
                                                                marginBottom: .5,
                                                                display: isHidden ? 'none' : 'block',
                                                                ...boxStyle(snapshot.isDragging, isSelected(i)),
                                                                '&:hover': {
                                                                    background: '#F3F3F3',
                                                                    cursor: 'pointer',
                                                                    boxShadow: '2px 2px 10px 0px rgba(0,0,0,0.3)',
                                                                    borderColor: '#888888',
                                                                }
                                                            }}>
                                                            <ListItemText
                                                                primary={
                                                                    <Stack direction="row" alignItems="start" spacing={.5} justifyContent='space-between'>
                                                                        <Stack direction="column" spacing={0}>
                                                                            <Stack direction="row" alignItems="center" spacing={.5}>
                                                                                {showItemCheckbox && (
                                                                                    (checkableItemIds === null || _.includes(checkableItemIds, order.id)) ? (
                                                                                        <Checkbox
                                                                                            size="small"
                                                                                            checked={_.includes(checkedItemIds, order.id)}
                                                                                            // El item entero es drag handle y además
                                                                                            // selecciona la caja al hacer click: sin frenar
                                                                                            // el evento, marcar el checkbox arrastraba
                                                                                            // o seleccionaba el pedido.
                                                                                            onMouseDown={(e) => e.stopPropagation()}
                                                                                            onClick={(e) => e.stopPropagation()}
                                                                                            onChange={() => onToggleItemChecked(order.id)}
                                                                                            sx={{ p: 0, mr: 0.5 }}
                                                                                        />
                                                                                    ) : (
                                                                                        <Tooltip title={uncheckableReason}>
                                                                                            {/* En el lugar del checkbox para que la fila no
                                                                                                se descuadre. El span recibe el hover: un
                                                                                                icono deshabilitado no dispara el tooltip. */}
                                                                                            <Box
                                                                                                component="span"
                                                                                                onMouseDown={(e) => e.stopPropagation()}
                                                                                                onClick={(e) => e.stopPropagation()}
                                                                                                sx={{ display: 'inline-flex', p: 0, mr: 0.5, cursor: 'not-allowed' }}
                                                                                            >
                                                                                                <BlockOutlinedIcon sx={{ fontSize: 18, color: '#BDBDBD' }} />
                                                                                            </Box>
                                                                                        </Tooltip>
                                                                                    )
                                                                                )}
                                                                                <PlaceIcon
                                                                                    color={ order.locatable?.has_location ? 'success' : 'error' }
                                                                                    sx={{ fontSize: 14, mr: 0.5 }}
                                                                                />

                                                                                <Typography
                                                                                    variant='h5'
                                                                                    mb={.2}
                                                                                    sx={{ display: 'inline'}}
                                                                                    component="span"
                                                                                >
                                                                                    {order.code}
                                                                                </Typography>

                                                                                {/* Mismo origen y tipo de documento que en Pedidos */}
                                                                                <OrderSourceBadges order={order} size="small" />

                                                                                {order.plugin_source === 'mercado-libre' && (
                                                                                    <>
                                                                                        <img
                                                                                            src="/images/icons/mercado-libre-color.svg"
                                                                                            alt="Mercado Libre"
                                                                                            style={{ height: 16, marginLeft: 4 }}
                                                                                        />
                                                                                        {(() => {
                                                                                            const sm = String(order.shipping_method || '').toLowerCase().trim();
                                                                                            const lt = String(order.logistic_type || '').toLowerCase().trim();

                                                                                            if (sm === 'fulfillment') {
                                                                                                return (
                                                                                                    <img 
                                                                                                        src="/images/icons/meli-full.svg" 
                                                                                                        alt="Full" 
                                                                                                        style={{ height: 18, marginLeft: 4 }} 
                                                                                                    />
                                                                                                );
                                                                                            }

                                                                                            if (sm === 'me2' && lt === 'self_service') {
                                                                                                return (
                                                                                                    <img 
                                                                                                        src="/images/icons/meli-flex.svg" 
                                                                                                        alt="Flex" 
                                                                                                        style={{ height: 20, marginLeft: 4 }} 
                                                                                                    />
                                                                                                );
                                                                                            }

                                                                                            if (lt === 'xd_drop_off') {
                                                                                                return (
                                                                                                    <img 
                                                                                                        src="/images/icons/meli-dropoff.svg" 
                                                                                                        alt="Drop Off" 
                                                                                                        style={{ height: 16, marginLeft: 4 }} 
                                                                                                    />
                                                                                                );
                                                                                            }

                                                                                            if (lt === 'pickup') {
                                                                                                return (
                                                                                                    <img 
                                                                                                        src="/images/icons/meli-pickup.svg" 
                                                                                                        alt="Pickup" 
                                                                                                        style={{ height: 16, marginLeft: 4 }} 
                                                                                                    />
                                                                                                );
                                                                                            }

                                                                                            return null;
                                                                                        })()}
                                                                                    </>
                                                                                )}

                                                                                {/* Aviso de retiro para cualquier origen. Acá es lo más
                                                                                    importante: es la vista donde se arman los viajes y un
                                                                                    retiro no debe entrar a uno. Se excluye meli porque su
                                                                                    propio bloque ya pinta este ícono. */}
                                                                                {order.plugin_source !== 'mercado-libre' &&
                                                                                    String(order.shipping_method || '').toLowerCase().trim() === 'pickup' && (
                                                                                    <img
                                                                                        src="/images/icons/meli-pickup.svg"
                                                                                        alt="Retiro"
                                                                                        style={{ height: 16, marginLeft: 4 }}
                                                                                    />
                                                                                )}
                                                                            </Stack>

                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{
                                                                                    color: "text.primary",
                                                                                    fontSize: 14,
                                                                                    ml: 0
                                                                                }}
                                                                            >
                                                                                {order.locatable?.name}
                                                                            </Typography>

                                                                        </Stack>

                                                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                                                            {hasMissingBarcode && (
                                                                                <Tooltip title="Esta orden contiene productos sin código de barras">
                                                                                    <i
                                                                                        className="fa-solid fa-barcode"
                                                                                        style={{
                                                                                            fontSize: 16,
                                                                                            color: '#d32f2f'
                                                                                        }}
                                                                                    />
                                                                                </Tooltip>
                                                                            )}

                                                                            <Typography 
                                                                                variant="body2" 
                                                                                color={"text.secondary"} 
                                                                                noWrap 
                                                                                sx={{ maxWidth: 85 }} 
                                                                                align="right"
                                                                            >
                                                                                {_.sumBy(order.order_details, 'pending_qty')} items
                                                                            </Typography>
                                                                        </Stack>

                                                                    </Stack>
                                                                }
                                                                secondary={
                                                                    <React.Fragment>
                                                                        <Stack direction="row" justifyContent="space-between">
                                                                            {isAgreedAddress(order) ? (
                                                                                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                                                                                    <Tooltip title="Dirección acordada con el comprador en Mercado Libre">
                                                                                        <Chip label="Acordada" size="small" color="info" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                                                                                    </Tooltip>
                                                                                    <Typography noWrap>
                                                                                        {[order.effective_location?.short_address?.trim(), order.effective_location?.short_zone].filter(Boolean).join(', ') || '-'}
                                                                                    </Typography>
                                                                                </Stack>
                                                                            ) : (
                                                                                <Typography>
                                                                                    {(order.effective_location || order.locatable?.location)?.short_zone || '-'}
                                                                                </Typography>
                                                                            )}

                                                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    color="text.secondary"
                                                                                    ml={1}
                                                                                >
                                                                                    {moment(order.ship_date).utc().format('DD MMM')}
                                                                                </Typography>
                                                                                <CalendarMonthTwoToneIcon
                                                                                    fontSize="small"
                                                                                    color="primary"
                                                                                />
                                                                            </Stack>
                                                                        </Stack>

                                                                        {/* Fecha de entrega del ERP, igual que en Pedidos: aca es donde
                                                                            mas importa, porque se decide en que viaje va el pedido. */}
                                                                        {erpDeliveryInfo(order) && (
                                                                            <Stack direction="row" justifyContent="flex-end">
                                                                                <Tooltip title={erpDeliveryInfo(order).titulo} arrow>
                                                                                    <Typography variant="caption" color={erpDeliveryInfo(order).color} sx={{ fontWeight: 600 }}>
                                                                                        {erpDeliveryInfo(order).texto}
                                                                                    </Typography>
                                                                                </Tooltip>
                                                                            </Stack>
                                                                        )}

                                                                        {/* Quien despacha el pedido (Bluexpress, Starken...), tal como
                                                                            viene del ERP o del canal de venta. */}
                                                                        {(() => {
                                                                            const carrier = getDropoffCarrierDisplay(order.dropoff_carrier, dropoffCarrierCatalog);
                                                                            if (!carrier) return null;

                                                                            return (
                                                                                <Tooltip title={`Despacha: ${carrier.label}`}>
                                                                                    <Chip
                                                                                        size="small"
                                                                                        variant="outlined"
                                                                                        label={carrier.label}
                                                                                        icon={carrier.iconPath ? (
                                                                                            <Box
                                                                                                component="img"
                                                                                                src={carrier.iconPath}
                                                                                                alt=""
                                                                                                sx={{ height: 14, maxWidth: 32, objectFit: 'contain' }}
                                                                                            />
                                                                                        ) : (
                                                                                            <LocalShippingOutlinedIcon sx={{ fontSize: 16 }} />
                                                                                        )}
                                                                                        sx={{ mt: 0.5, height: 22, fontSize: 11, pl: 0.5 }}
                                                                                    />
                                                                                </Tooltip>
                                                                            );
                                                                        })()}

                                                                        {isRoute && order.route_error && (
                                                                            <Alert
                                                                                severity="error"
                                                                                sx={{
                                                                                    mt: 1,
                                                                                    '&.MuiAlert-root': {
                                                                                        px: 0.75,
                                                                                        py: 0,
                                                                                        fontSize: 11,
                                                                                    },
                                                                                }}
                                                                            >
                                                                                {order.route_error}
                                                                            </Alert>
                                                                        )}

                                                                        {isRoute && (
                                                                            <Stack
                                                                                direction="row"
                                                                                justifyContent="space-between"
                                                                                alignItems="center"
                                                                                mt={1}
                                                                            >
                                                                                {/* Comentario (opcional) */}
                                                                                {order.comment ? (
                                                                                    <Alert
                                                                                        severity="info"
                                                                                        icon={
                                                                                            <CommentOutlinedIcon sx={{ fontSize: 16 }} />
                                                                                        }
                                                                                        sx={{
                                                                                            flex: 1,
                                                                                            mr: 1,
                                                                                            '&.MuiAlert-root': {
                                                                                                px: 0.5,
                                                                                                py: 0,
                                                                                                fontSize: 11,
                                                                                            },
                                                                                        }}
                                                                                    >
                                                                                        {order.comment}
                                                                                    </Alert>
                                                                                ) : (
                                                                                    <div />
                                                                                )}

                                                                                {showDropoffCarrierField && (
                                                                                    <IconButton
                                                                                        size="small"
                                                                                        disabled={!mapPickupMode}
                                                                                        onClick={(e) => {
                                                                                            e.preventDefault();
                                                                                            e.stopPropagation();
                                                                                        }}
                                                                                    >
                                                                                        {(() => {
                                                                                            const dropoffCarrier = getDropoffCarrierByKey(order.dropoff_carrier, dropoffCarrierCatalog);

                                                                                            if (order.dropoff && dropoffCarrier?.iconPath) {
                                                                                                return (
                                                                                                    <Tooltip title={`Punto de dropoff - ${dropoffCarrier.label || order.dropoff_carrier}`}>
                                                                                                        <img
                                                                                                            src={dropoffCarrier.iconPath}
                                                                                                            alt={dropoffCarrier.label || order.dropoff_carrier}
                                                                                                            style={{ height: 22 }}
                                                                                                        />
                                                                                                    </Tooltip>
                                                                                                );
                                                                                            }

                                                                                            return (
                                                                                                <CheckCircleOutlineIcon
                                                                                                    sx={{
                                                                                                        fontSize: 18,
                                                                                                        color: '#999',
                                                                                                    }}
                                                                                                />
                                                                                            );
                                                                                        })()}
                                                                                    </IconButton>
                                                                                )}
                                                                            </Stack>
                                                                        )}
                                                                    </React.Fragment>
                                                                }

                                                            />
                                                        </ListItem>
                                                    )
                                                }}
                                            </Draggable>
                                        )
                                    })
                                }
                                {provided.placeholder}
                            </DNDList>
                        )}
                    </Droppable>
                    {loadingMoreItems && (
                        <Div sx={{ px: 1, pb: 1 }}>
                            <LinearProgress />
                            <Typography variant="caption" color="text.secondary">
                                Cargando más pedidos...
                            </Typography>
                        </Div>
                    )}
                </JumboScrollbar>
            }
            {newGroupContainer === true &&
                <Droppable droppableId={droppableId}>
                    {(provided, snapshot) => (
                        <DNDList disablePadding {...provided.droppableProps} ref={provided.innerRef} isDraggingOver={snapshot.isDraggingOver}>
                            <ListItem
                                dense={true}
                                key={`newtrip_1`}
                                ref={provided.innerRef}>
                                <ListItemText
                                    secondary={
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography>Arrastre órdenes aquí para crear una nueva ruta</Typography>
                                        </Stack>
                                    }
                                />
                            </ListItem>
                        </DNDList>
                    )}
                </Droppable>
            }
        </JumboCardQuick>
    )
}

export default OrdersDND;
